// hookRegistry.js — wire-dead-engines E6-WS1/WS2.
// @arch:pure-engine
//
// Kiro "agent hooks" for LOCAL IDE events only: fire an agent prompt or a shell command
// when an event occurs (on-save / on-commit / pre-post-task / pre-post-tool / turn-completion
// / manual). Pre/Post-Task bind to the E3 executor's task events. NO external triggers
// (Slack/GitHub/Sentry) — those are gated/out (03-PLAN §GATED).
//
// PURE (Tier 1): matcher + runner over INJECTED ports (runCommand / runAgent). No react,
// no window. The renderer wraps this with localStorage persistence + the apolo:taskGraph
// binding. An EMPTY registry ⇒ no hooks fire ⇒ byte-identical to pre-sprint (default-safe).

export const HOOK_EVENTS = Object.freeze([
  'onSave', 'onCreate', 'onDelete', 'onCommit',
  'preTask', 'postTask', 'preTool', 'postTool',
  'turnCompletion', 'manual',
]);
export const HOOK_ACTIONS = Object.freeze(['shell', 'ask']);

// Map the executor's frozen TASK_EVENTS → hook event names (the live binding uses this).
export const EXECUTOR_EVENT_TO_HOOK = Object.freeze({
  task_started: 'preTask',
  task_oracle_pass: 'postTask',
});

/** Tiny glob → RegExp (`*` = any run, `?` = one char). Anchored, case-insensitive. */
function globToRegex(glob) {
  const esc = String(glob).replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.');
  return new RegExp(`^${esc}$`, 'i');
}

/** Normalize a raw hook config; returns null when invalid (unknown event/action). */
export function normalizeHook(raw, i = 0) {
  if (!raw || typeof raw !== 'object') return null;
  const event = String(raw.event || '').trim();
  if (!HOOK_EVENTS.includes(event)) return null;
  const action = HOOK_ACTIONS.includes(raw.action) ? raw.action : 'shell';
  const instructions = String(raw.instructions ?? raw.command ?? raw.prompt ?? '').trim();
  if (!instructions) return null;
  return {
    id: String(raw.id || `hook_${event}_${i}`),
    title: String(raw.title || instructions.slice(0, 40)),
    description: String(raw.description || ''),
    event,
    toolName: raw.toolName ? String(raw.toolName) : null,
    filePattern: raw.filePattern ? String(raw.filePattern) : null,
    action,
    instructions,
  };
}

/**
 * The hooks that match an event. event = { type, toolName?, filePath? }.
 * Scopes by event type, then optional toolName (glob/exact) + filePattern (glob).
 */
export function matchHooks(hooks, event) {
  const list = (Array.isArray(hooks) ? hooks : []).map((h, i) => normalizeHook(h, i)).filter(Boolean);
  const type = event && event.type;
  return list.filter((h) => {
    if (h.event !== type) return false;
    if (h.toolName && !(event.toolName && globToRegex(h.toolName).test(event.toolName))) return false;
    if (h.filePattern && !(event.filePath && globToRegex(h.filePattern).test(event.filePath))) return false;
    return true;
  });
}

/**
 * Run every matched hook EXACTLY ONCE for this event. shell → runCommand; ask → runAgent.
 * Best-effort: a hook that throws is captured in its result, never aborts the others.
 * @returns {Promise<Array<{id,action,ok,result?,error?}>>}
 */
export async function runMatchedHooks(hooks, event, { runCommand, runAgent, root } = {}) {
  const matched = matchHooks(hooks, event);
  const out = [];
  for (const h of matched) {
    try {
      if (h.action === 'shell') {
        if (typeof runCommand !== 'function') { out.push({ id: h.id, action: h.action, ok: false, error: 'no runCommand' }); continue; }
        const r = await runCommand({ command: h.instructions, cwd: root });
        out.push({ id: h.id, action: 'shell', ok: (r?.exitCode ?? 0) === 0, result: r });
      } else {
        if (typeof runAgent !== 'function') { out.push({ id: h.id, action: h.action, ok: false, error: 'no runAgent' }); continue; }
        const r = await runAgent({ task: h.instructions });
        out.push({ id: h.id, action: 'ask', ok: r?.ok !== false, result: r });
      }
    } catch (e) {
      out.push({ id: h.id, action: h.action, ok: false, error: e?.message || String(e) });
    }
  }
  return out;
}

// A command-ish token at the start ⇒ treat the instruction as a shell command.
const CMD_HINT = /\b(npm |npx |yarn |pnpm |vitest|eslint|tsc\b|jest|pytest|go test|cargo |make |git )/i;

/**
 * NL → hook config (Kiro's "describe a hook, get the JSON"). Heuristic + pure.
 * Detects the event from phrasing and the action from whether a command is named.
 * @returns {object|null} a normalized hook, or null when no event could be inferred.
 */
export function hookFromDescription(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  let event = null;
  if (/\b(after|post)[\s-]*(each\s+)?task\b/i.test(t)) event = 'postTask';
  else if (/\b(before|pre)[\s-]*(each\s+)?task\b/i.test(t)) event = 'preTask';
  else if (/\bon\s+commit\b|\b(after|pre|post)[\s-]*commit\b/i.test(t)) event = 'onCommit';
  else if (/\bon\s+save\b|\bwhen.*\bsave/i.test(t)) event = 'onSave';
  else if (/\b(after|post)[\s-]*tool\b/i.test(t)) event = 'postTool';
  else if (/\b(before|pre)[\s-]*tool\b/i.test(t)) event = 'preTool';
  else if (/\bturn\b/i.test(t)) event = 'turnCompletion';
  if (!event) return null;

  const cmdMatch = t.match(/[:—-]\s*(.+)$/);
  const inner = cmdMatch ? cmdMatch[1].trim() : t;
  const action = CMD_HINT.test(inner) ? 'shell' : 'ask';
  return normalizeHook({ event, action, instructions: action === 'shell' ? inner : t, title: t.slice(0, 48) });
}
